import * as React from 'react';
import { modal } from './Configure.style';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import { TransposedColumns } from 'configuration-schemas/viewdefns/general';
import { isNil } from 'lodash';

export interface Option {
  dataIndex: string;
  text?: string;
  type?: string;
  renderer?: string;
  colDef?: string;
  groupingKey?: string;
}

export interface OptionGroup {
  text?: string;
  options: Option[];
}

export type ToggleAction = 'close' | 'apply';

export interface EnabledProps {
  enabled: true;
  isOpen: boolean;
  optionGroups: OptionGroup[];
  selections: Option[];
  instructions?: string;
  minimumSelections?: number;
  transposedColumns?: TransposedColumns;
  transposedSelections?: Option[];
  onToggleModal(action: ToggleAction): void;
  onReset(): void;
  selectionUpdate(selections: Option[], transposedSelections?: Option[]): void;
}

export interface DisabledProps {
  enabled: false;
}
export type ConfigureModalProps = EnabledProps | DisabledProps;

export interface Selection {
  dataIndex: string;
  selections: Option[];
  text: string;
}

export interface ConfigureModalState {
  searchValue: string;
}

export default class ConfigureModal extends React.Component<ConfigureModalProps, ConfigureModalState> {
  constructor(props: ConfigureModalProps) {
    super(props);
    this.state = { searchValue: '' };
    this.optionSelected = this.optionSelected.bind(this);
    this.optionClear = this.optionClear.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleClearSearch = this.handleClearSearch.bind(this);
  }

  handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ searchValue: event.currentTarget.value });
  }

  handleClearSearch() {
    this.setState({ searchValue: '' });
  }

  optionSelected(groupIndex: number, selected: Option) {
    if (!this.props.enabled) {
      return;
    }
    const { selections, selectionUpdate } = this.props;
    const newSelections = [...selections];
    newSelections.splice(groupIndex, 1, selected);
    selectionUpdate(newSelections);
  }

  transposedOptionSelected(index: number, selected: Option) {
    if (!this.props.enabled) {
      return;
    }
    const { selections, transposedSelections, selectionUpdate } = this.props;

    if (isNil(transposedSelections)) return;
    const newSelections = [...transposedSelections];
    newSelections.splice(index, 1, selected);
    selectionUpdate(selections, newSelections);
  }

  optionClear(event: React.MouseEvent<HTMLSpanElement>) {
    if (!this.props.enabled) {
      return;
    }
    const { selections, selectionUpdate, minimumSelections } = this.props;
    event.stopPropagation();
    const newSelections = selections.slice(0, selections.length - 1);
    if (minimumSelections && newSelections.length < minimumSelections) {
      // some views don't allow less than a minumum number of aggBys
      return;
    }
    selectionUpdate(selections.slice(0, selections.length - 1));
  }

  render() {
    if (!this.props.enabled) {
      return null;
    }

    const {
      isOpen,
      optionGroups,
      onToggleModal,
      onReset,
      selections,
      instructions,
      transposedColumns,
      transposedSelections,
    } = this.props;

    const className = modal + ' configure-modal';
    const { searchValue } = this.state;
    const normalizedSearch = searchValue.trim().toLowerCase();
    const matchesSearch = (text: string) => text.toLowerCase().includes(normalizedSearch);

    const aggLevelContent = optionGroups.map((optionGroup, groupIndex) => {
      const selectable = groupIndex <= selections.length;
      const optionGroupClasses = 'option-group' + (selectable ? ' selectable' : '');
      const selectionIds = new Set(selections.map((sel) => sel.dataIndex));
      const visibleOptions = normalizedSearch
        ? optionGroup.options.filter((option) => matchesSearch(option.text ?? ''))
        : optionGroup.options;

      return (
        <div className={optionGroupClasses} key={groupIndex} data-qa="row-level-config">
          <div className="overlay" />
          <header>{optionGroup.text}</header>
          <ul>
            {visibleOptions.map((option) => {
              const { text, dataIndex } = option;
              const selected = selections[groupIndex] && dataIndex === selections[groupIndex].dataIndex;
              const clearable = selected && (groupIndex === selections.length || groupIndex === selections.length - 1);
              const itemDisabled = selectionIds.has(dataIndex) && !selected;

              const itemClassName = 'option' + (selected ? ' selected' : '') + (itemDisabled ? ' disabled' : '');

              return (
                <li
                  className={itemClassName}
                  key={dataIndex}
                  onClick={(event) => {
                    if (selectable && !itemDisabled) {
                      this.optionSelected(groupIndex, option);
                    }

                    if (clearable) {
                      this.optionClear(event);
                    }
                  }}
                >
                  {text}
                </li>
              );
            })}
          </ul>
        </div>
      );
    });

    // Build timeLevelContent
    let timeLevelContent: JSX.Element[] | null = null;
    if (!isNil(transposedColumns) && !isNil(transposedSelections) && transposedSelections.length > 0) {
      const { topLevel, bottomLevel, topLevelTitle, bottomLevelTitle } = transposedColumns;
      const selectionIds = new Set(transposedSelections?.map((sel) => sel.dataIndex));

      timeLevelContent = transposedSelections.map((selec, index) => {
        const selectable = index <= selections.length;
        const levelOptions = index === 0 ? topLevel : bottomLevel;
        const visibleOptions = normalizedSearch
          ? levelOptions.filter((option) => matchesSearch(option.text))
          : levelOptions;

        return (
          <div className={'option-group selectable'} key={index} data-qa="header-level-config">
            <div className="overlay" />
            <header>{index === 0 ? topLevelTitle : bottomLevelTitle}</header>
            <ul>
              {visibleOptions.map((option, ind) => {
                const { text, dataIndex } = option;
                const selected = dataIndex === selec.dataIndex;
                const itemDisabled = selectionIds.has(dataIndex) && !selected;

                const itemClassName = 'option' + (selected ? ' selected' : '') + (itemDisabled ? ' disabled' : '');

                return (
                  <li
                    className={itemClassName}
                    key={ind}
                    onClick={(event) => {
                      if (selectable && !itemDisabled) {
                        this.transposedOptionSelected(index, option);
                      }
                    }}
                  >
                    {text}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      });
    }

    const mainContent = !isNil(timeLevelContent) ? (
      <>
        {timeLevelContent}
        <Divider orientation="vertical" flexItem variant="middle" />
        {aggLevelContent}
      </>
    ) : (
      aggLevelContent
    );

    return (
      <Dialog
        open={isOpen}
        onClose={() => onToggleModal('close')}
        classes={{ root: className }}
        fullWidth={true}
        scroll={'paper'}
        maxWidth={'xl'}
      >
        <DialogTitle>
          <span className="left-container">
            <i className="fas fa-cog icon" />
            Configure
          </span>
          <span className="right-container">
            <i className="far fa-times" onClick={() => onToggleModal('close')} />
          </span>
        </DialogTitle>
        <DialogContent dividers>
          <div className="configure-header-row">
            <p className="instructions">{instructions}</p>
            <OutlinedInput
              className="configure-search"
              aria-label="Search options"
              value={searchValue}
              onChange={this.handleSearchChange}
              placeholder="Search options"
              inputProps={{ 'data-qa': 'configure-search-input' }}
              autoFocus
              startAdornment={
                <InputAdornment position="start">
                  <i className="far fa-search" />
                </InputAdornment>
              }
              endAdornment={
                searchValue && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={this.handleClearSearch} aria-label="Clear search">
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </div>
          <main data-qa="configure-content-container">{mainContent}</main>
          <footer>
            <SquareActionButton
              onClick={() => onToggleModal('apply')}
              text="Apply"
              icon="far fa-check"
              type="primary"
            />
            <SquareActionButton onClick={onReset} text="Reset" icon="fa fa-undo" type="secondary" />
          </footer>
        </DialogContent>
      </Dialog>
    );
  }
}
